'use client';

import { getCookie } from 'cookies-next';
import React, {
  createContext,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

// utils
import { findStudioById } from '@/components/molecules/ml-search-location-form/utils';
import { ILocationData } from '@/components/helpers/fetchInitialProps';
import { updateSharedCookieFields } from '@/utils/cookieUtils';

// types
import { IDesignStudio } from '@/components/organisms/or-design-studio-block/or-design-studio-block.types';

// Define the shape of the Location context
interface ILocationContextType {
  studioId: number | null;
  setStudioId: React.Dispatch<SetStateAction<number | null>>;
  currentBuildStudio: IDesignStudio | null;
  allLocations: ILocationData[];
  allStudios: IDesignStudio[];
  onItemClick: (cId: number | null, selected: string | null) => void;
  selectedText: string | null;
  getStudioById: (sId: number | null) => IDesignStudio[];
  clearLocation: () => void;
  hasLoadedLocation: boolean;
}

// Default context values (no-op functions and empty data)
const defaultContextValue: ILocationContextType = {
  studioId: null,
  setStudioId: () => {},
  currentBuildStudio: null,
  allLocations: [],
  allStudios: [],
  hasLoadedLocation: false,
  onItemClick: () => {},
  selectedText: null,
  getStudioById: () => [],
  clearLocation: () => {},
};

// Create the context with default values
const LocationContext =
  createContext<ILocationContextType>(defaultContextValue);

// Hook to consume the location context
export const useLocation = () => useContext(LocationContext);

// Props for the LocationProvider
interface ILocationProviderProps {
  children: ReactNode;
  locationData: ILocationData[];
  studioData: IDesignStudio[];
}

// LocationProvider to manage and provide location-related state
export const LocationProvider = ({
  children,
  locationData,
  studioData,
}: ILocationProviderProps) => {
  const [studioId, setStudioId] = useState<number | null>(null); // Selected studio ID
  const [currentBuildStudio, setCurrentBuildStudio] =
    useState<IDesignStudio | null>(null); // Currently selected studio details
  const [allLocations, setAllLocations] =
    useState<ILocationData[]>(locationData); // List of available locations
  const [allStudios, setAllStudios] = useState<IDesignStudio[]>(studioData); // List of design studios
  const [selectedText, setSelectedText] = useState<string | null>(null); // User-facing location label
  const [hasLoadedLocation, setHasLoadedLocation] = useState(false);

  /**
   * Handles user interaction with a location/studio item.
   * Updates local state and writes data to shared cookie.
   */
  const handleElementClick = (
    newStudioId: number | null,
    selected: string | null,
  ) => {
    const studio = newStudioId ? findStudioById(newStudioId, allStudios) : null;

    setStudioId(newStudioId);
    setSelectedText(selected ?? null);
    setCurrentBuildStudio(studio ?? null);

    // Update shared cookie with selected studio/location details
    updateSharedCookieFields({
      StudioId: newStudioId,
      SelectedLocation: selected ?? null,
    });
  };

  useEffect(() => {
    const raw = getCookie('MySchumacherIsShared');
    if (!raw) {
      setHasLoadedLocation(true);
      return;
    }

    try {
      const parsed = JSON.parse(raw as string);
      const id = parsed.StudioId ?? null;
      const text = parsed.SelectedLocation ?? null;
      const studio = id ? findStudioById(id, studioData) : null;

      setStudioId(id);
      setSelectedText(text);
      setCurrentBuildStudio(studio ?? null);
    } catch {
      // Silently ignore if cookie parsing fails
    } finally {
      setHasLoadedLocation(true);
    }
  }, [studioData]);

  /**
   * Returns a filtered list of studios by ID.
   * If no ID is given, returns all studios.
   */
  const getStudioById = (id: number | null): IDesignStudio[] => {
    if (!id) return allStudios;
    return allStudios.filter((studio) => studio.Id === id);
  };

  /**
   * Clears selected location/studio data from local state.
   * Does not update the cookie.
   */
  const clearLocation = () => {
    setStudioId(null);
    setSelectedText(null);
    setCurrentBuildStudio(null);
  };

  // Provide location state and utilities to consumers
  return (
    <LocationContext.Provider
      value={{
        studioId,
        setStudioId,
        currentBuildStudio,
        allLocations,
        allStudios,
        clearLocation,
        onItemClick: handleElementClick,
        selectedText,
        getStudioById,
        hasLoadedLocation,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};
